import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useMemo } from 'react'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { getDetailProfile } from '@/services/sez-regions'
import { BaseRequest } from '@/types/sez-regions'
import clsxm from '@/utils/clsxm'
import { useAppSelector } from '@/utils/hooks'

import AreaIcon from '~/assets/icons/area-icon.svg'
import DeveloperIcon from '~/assets/icons/developer-icon.svg'
import InvestmentTargetIcon from '~/assets/icons/investment-target-icon.svg'
import LegalBasisIcon from '~/assets/icons/legal-basis-icon.svg'
import PopulationIcon from '~/assets/icons/population-icon.svg'
import TargetWorkforceIcon from '~/assets/icons/target-workforce-icon.svg'

const Profile: React.FC = () => {
  const { t } = useTranslation()

  const router = useRouter()

  const request: BaseRequest = useMemo(() => {
    return {
      params: { slug: String(router.query?.slug) },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data } = useAppSelector(getDetailProfile.select(request))

  const details = [
    {
      id: 0,
      name: 'legal',
      label: t('common:legalBasis'),
      icon: <LegalBasisIcon className='h-10.5 w-10.5' />,
      value: data?.data.legalBasis,
    },
    {
      id: 1,
      name: 'developer',
      label: t('common:developer'),
      icon: <DeveloperIcon className='h-10.5 w-10.5' />,
      value: data?.data.developer,
    },
    {
      id: 2,
      name: 'area',
      label: t('common:area'),
      icon: <AreaIcon className='h-10.5 w-10.5' />,
      value: data?.data.area,
    },
    {
      id: 3,
      name: 'population',
      label: t('common:population'),
      icon: <PopulationIcon className='h-10.5 w-10.5' />,
      value: data?.data.population,
    },
    {
      id: 4,
      name: 'investmentTarget',
      label: t('common:investmentTarget'),
      icon: <InvestmentTargetIcon className='h-10.5 w-10.5' />,
      value: data?.data.investmentTarget,
    },
    {
      id: 5,
      name: 'targetWorkforce',
      label: t('common:workforceTarget'),
      icon: <TargetWorkforceIcon className='h-10.5 w-10.5' />,
      value: data?.data.targetWorkforce,
    },
  ]

  return (
    <div className='container flex w-full flex-col gap-6 lg:flex-row lg:gap-7.5'>
      <div className='flex flex-1 flex-col gap-4 lg:gap-6'>
        {data && data.data.imageFiles ? (
          <div className='relative w-full lg:h-[357.505px]'>
            <div className='absolute inset-0' />
            <ImageByWidthDevice
              images={data.data.imageFiles}
              alt='Profile Image'
              className='h-full w-full object-cover object-bottom'
            />
          </div>
        ) : null}

        <div
          className='description break-words text-sm tracking-[-0.176px] text-gray-1 lg:text-base'
          dangerouslySetInnerHTML={{ __html: data?.data.description ?? '' }}
        />
      </div>

      <div className='shrink-0 md:basis-[445px]'>
        <div className='flex flex-col'>
          {details.map((item, index) => (
            <div
              key={item.id}
              className={clsxm(
                'flex gap-4 border border-neutral-border p-3.5',
                index == 0 && 'rounded-t-lg',
                index == details.length - 1 && 'rounded-b-lg'
              )}
            >
              <div className='5 basis-10'>{item.icon}</div>

              <div className='flex flex-col gap-1'>
                <small className='text-xs text-gray-1'>{item.label}</small>
                <p className='text-sm font-semibold tracking-[-0.084px] text-gray-1'>{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Profile
